"use client";

import { Copy, Download, Loader2, Save, ShieldCheck } from "lucide-react";
import dynamic from "next/dynamic";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Switch } from "~/components/ui/switch";
import type { Changelog } from "~/data/changelog-schema";
import { cn } from "~/utils/cn";
import { ChangelogTimeline } from "./changelog-timeline";
import { useSaveChangelog } from "./use-changelog";
import { VersionHeader } from "./version-header";

const ChangelogJsonEditor = dynamic(() => import("./json-editor"), {
	ssr: false,
	loading: () => (
		<div className="flex h-full items-center justify-center text-muted-foreground text-sm">
			<Loader2 className="mr-2 h-4 w-4 animate-spin" />
			Loading editor…
		</div>
	),
});

export const ChangelogEditor = ({
	changelog,
	passkey,
}: {
	changelog: Changelog;
	passkey: string | null;
}) => {
	const [data, setData] = useState<Changelog>(changelog);
	const [previewVersion, setPreviewVersion] = useState(
		changelog.currentVersion
	);
	const [showInternal, setShowInternal] = useState(true);

	const save = useSaveChangelog(passkey);

	const handleSave = async () => {
		try {
			const result = await save.mutateAsync(data);
			// Reflect the server-stamped updatedAt in the (read-only) editor field.
			setData((previous) => ({ ...previous, updatedAt: result.updatedAt }));
			if (result.persisted === false) {
				toast.warning(
					"Saved in memory, but the server file is read-only. Use Copy or Download and commit the file (or run the changelog skill)."
				);
			} else {
				toast.success("Changelog saved.");
			}
		} catch (saveError) {
			toast.error(
				saveError instanceof Error ? saveError.message : "Save failed."
			);
		}
	};

	const handleCopy = async () => {
		await navigator.clipboard.writeText(JSON.stringify(data, null, "\t"));
		toast.success("Changelog JSON copied to clipboard.");
	};

	const handleDownload = () => {
		const json = `${JSON.stringify(data, null, "\t")}\n`;
		const blob = new Blob([json], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = "changelog.json";
		anchor.click();
		URL.revokeObjectURL(url);
	};

	const activeVersion =
		data.versions.find((version) => version.version === previewVersion) ??
		data.versions[0];

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex flex-wrap items-center justify-between gap-2 border-border/50 border-b px-4 py-2.5">
				<span className="inline-flex items-center gap-1.5 font-medium text-muted-foreground text-xs">
					<ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
					Schema-validated — invalid edits are blocked inline
				</span>
				<div className="flex items-center gap-2">
					<Button
						className="h-8 gap-1.5 px-2.5 text-xs"
						onClick={handleCopy}
						size="sm"
						type="button"
						variant="ghost"
					>
						<Copy className="h-3.5 w-3.5" />
						Copy
					</Button>
					<Button
						className="h-8 gap-1.5 px-2.5 text-xs"
						onClick={handleDownload}
						size="sm"
						type="button"
						variant="ghost"
					>
						<Download className="h-3.5 w-3.5" />
						Download
					</Button>
					<Button
						className="h-8 gap-1.5 px-3 text-xs"
						disabled={save.isPending}
						onClick={handleSave}
						size="sm"
						type="button"
					>
						{save.isPending ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<Save className="h-3.5 w-3.5" />
						)}
						Save
					</Button>
				</div>
			</div>

			<div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_minmax(320px,42%)]">
				<div className="min-h-0 border-border/50 border-r">
					<ScrollArea className="h-full">
						<div className="p-4">
							<ChangelogJsonEditor data={data} onChange={setData} />
						</div>
					</ScrollArea>
				</div>
				<div className="flex min-h-0 flex-col bg-muted/10">
					<div className="flex items-center justify-between gap-2 border-border/50 border-b px-4 py-2">
						<select
							aria-label="Preview version"
							className="rounded-md border border-border bg-background px-2 py-1 text-xs"
							onChange={(event) => setPreviewVersion(event.target.value)}
							value={activeVersion?.version ?? ""}
						>
							{data.versions.map((version) => (
								<option key={version.version} value={version.version}>
									v{version.version}
								</option>
							))}
						</select>
						<div
							className={cn(
								"flex items-center gap-2 text-[11px]",
								showInternal ? "text-foreground" : "text-muted-foreground"
							)}
						>
							<span id="changelog-show-internal-label">Show internal</span>
							<Switch
								aria-labelledby="changelog-show-internal-label"
								checked={showInternal}
								onCheckedChange={setShowInternal}
							/>
						</div>
					</div>
					<ScrollArea className="min-h-0 flex-1">
						<div className="px-5 py-4">
							{activeVersion ? (
								<>
									<VersionHeader showEmptyHints version={activeVersion} />
									<ChangelogTimeline
										showInternal={showInternal}
										version={activeVersion}
									/>
								</>
							) : (
								<p className="py-8 text-center text-muted-foreground text-sm">
									Add a version to preview it here.
								</p>
							)}
						</div>
					</ScrollArea>
				</div>
			</div>
		</div>
	);
};
